import React, { useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, Search, Send, Clock } from 'lucide-react';
import { getApiKey } from '@/lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}

interface ReviewRequest {
  requestId: string;
  auditId: string;
  dataSubjectId: string;
  reason: string;
  status: string;
  createdAt: string;
}

interface ExplainResult {
  auditId: string;
  decision: string;
  rationale: string;
  inputs: string[];
  timestamp: string;
}

interface DataSubjectAction {
  auditId: string;
  action: string;
  timestamp: string;
  outcome: string;
}

export function GDPRView() {
  // Explain Decision state
  const [explainAuditId, setExplainAuditId] = useState('');
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainResult, setExplainResult] = useState<ExplainResult | null>(null);
  const [explainError, setExplainError] = useState('');

  // Review Requests state
  const [requestId, setRequestId] = useState('');
  const [reviewRequest, setReviewRequest] = useState<ReviewRequest | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');

  // Submit Review Request state
  const [submitForm, setSubmitForm] = useState({ auditId: '', dataSubjectId: '', reason: '' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Data Subject Lookup state
  const [lookupSubjectId, setLookupSubjectId] = useState('');
  const [subjectActions, setSubjectActions] = useState<DataSubjectAction[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const handleExplain = async () => {
    if (!explainAuditId.trim()) return;
    setExplainLoading(true);
    setExplainError('');
    setExplainResult(null);
    try {
      const res = await fetch(`/api/gdpr/explain/${encodeURIComponent(explainAuditId.trim())}`, {
        method: 'POST',
        headers: apiHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setExplainResult(data);
    } catch (err: any) {
      setExplainError(err.message ?? 'Request failed');
    } finally {
      setExplainLoading(false);
    }
  };

  const handleFetchRequest = async () => {
    if (!requestId.trim()) return;
    setRequestLoading(true);
    setRequestError('');
    setReviewRequest(null);
    try {
      const res = await fetch(`/api/gdpr/request/${encodeURIComponent(requestId.trim())}`, { headers: apiHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReviewRequest(data);
    } catch (err: any) {
      setRequestError(err.message ?? 'Request failed');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!submitForm.auditId.trim() || !submitForm.dataSubjectId.trim() || !submitForm.reason.trim()) return;
    setSubmitLoading(true);
    setSubmitResult('');
    setSubmitError('');
    try {
      const res = await fetch('/api/gdpr/request', {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify(submitForm),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmitResult(data.requestId ?? 'Submitted successfully');
      setSubmitForm({ auditId: '', dataSubjectId: '', reason: '' });
    } catch (err: any) {
      setSubmitError(err.message ?? 'Submission failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!lookupSubjectId.trim()) return;
    setLookupLoading(true);
    setLookupError('');
    setSubjectActions([]);
    try {
      const res = await fetch(`/api/gdpr/subject/${encodeURIComponent(lookupSubjectId.trim())}`, { headers: apiHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubjectActions(Array.isArray(data.actions) ? data.actions : []);
    } catch (err: any) {
      setLookupError(err.message ?? 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const statusColor = (status: string): string => {
    if (status === 'approved') return GovernanceColors.healthy;
    if (status === 'pending') return GovernanceColors.warning;
    if (status === 'denied') return GovernanceColors.critical;
    return '#A8A29E';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">GDPR Data Subject Rights</h2>

      <div className="grid grid-cols-2 gap-4">
        {/* Explain Decision */}
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldAlert aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.primaryLight }} />
              <h3 className="text-sm font-medium">Explain Decision</h3>
            </div>
            <p className="text-xs text-[#8F857B]">Article 22 -- right to explanation for automated decisions.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={explainAuditId}
                onChange={(e) => setExplainAuditId(e.target.value)}
                placeholder="Audit ID"
                aria-label="Audit ID for decision explanation"
                className="flex-1 bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)]"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <Button
                size="sm"
                onClick={handleExplain}
                disabled={explainLoading || !explainAuditId.trim()}
                aria-label="Fetch decision explanation"
                className="text-white"
              >
                {explainLoading ? 'Loading...' : 'Explain'}
              </Button>
            </div>
            {explainError && (
              <p role="alert" className="text-xs" style={{ color: GovernanceColors.critical }}>{explainError}</p>
            )}
            {explainResult && (
              <div className="space-y-1.5 bg-[#1C1917] rounded p-3">
                <p className="text-xs text-[#A8A29E]">Decision: <span className="text-[#F5F0E8]">{explainResult.decision}</span></p>
                <p className="text-xs text-[#A8A29E]">Rationale: <span className="text-[#F5F0E8]">{explainResult.rationale}</span></p>
                {explainResult.inputs?.length > 0 && (
                  <div>
                    <p className="text-xs text-[#A8A29E] mb-1">Inputs considered:</p>
                    <ul className="space-y-0.5">
                      {explainResult.inputs.map((inp, i) => (
                        <li key={i} className="text-xs text-[#F5F0E8] pl-2 border-l border-[#3A352F]">{inp}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-[#8F857B]">{new Date(explainResult.timestamp).toLocaleString()}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review Requests */}
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Clock aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.warning }} />
              <h3 className="text-sm font-medium">Review Requests</h3>
            </div>
            <p className="text-xs text-[#8F857B]">Look up a pending review request by ID.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
                placeholder="Request ID"
                aria-label="Review request ID"
                className="flex-1 bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)]"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <Button
                size="sm"
                onClick={handleFetchRequest}
                disabled={requestLoading || !requestId.trim()}
                aria-label="Fetch review request"
                className="text-white"
              >
                {requestLoading ? 'Loading...' : 'Fetch'}
              </Button>
            </div>
            {requestError && (
              <p role="alert" className="text-xs" style={{ color: GovernanceColors.critical }}>{requestError}</p>
            )}
            {reviewRequest && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-[#2E2A26]">
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Request ID</td>
                      <td className="py-1.5 font-mono text-[#F5F0E8]">{reviewRequest.requestId}</td>
                    </tr>
                    <tr className="border-b border-[#2E2A26]">
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Audit ID</td>
                      <td className="py-1.5 font-mono text-[#F5F0E8]">{reviewRequest.auditId}</td>
                    </tr>
                    <tr className="border-b border-[#2E2A26]">
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Subject ID</td>
                      <td className="py-1.5 text-[#F5F0E8]">{reviewRequest.dataSubjectId}</td>
                    </tr>
                    <tr className="border-b border-[#2E2A26]">
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Reason</td>
                      <td className="py-1.5 text-[#F5F0E8]">{reviewRequest.reason}</td>
                    </tr>
                    <tr className="border-b border-[#2E2A26]">
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Status</td>
                      <td className="py-1.5">
                        <Badge variant="outline" className="text-xs px-1 py-0" style={{ color: statusColor(reviewRequest.status), borderColor: statusColor(reviewRequest.status) }}>
                          {reviewRequest.status}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1.5 pr-3 text-[#A8A29E]">Created</td>
                      <td className="py-1.5 text-[#8F857B]">{new Date(reviewRequest.createdAt).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit Review Request */}
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Send aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.healthy }} />
              <h3 className="text-sm font-medium">Submit Review Request</h3>
            </div>
            <p className="text-xs text-[#8F857B]">Request human review of an automated decision.</p>
            <div className="space-y-2">
              <input
                type="text"
                value={submitForm.auditId}
                onChange={(e) => setSubmitForm((f) => ({ ...f, auditId: e.target.value }))}
                placeholder="Audit ID"
                aria-label="Audit ID for review request"
                className="w-full bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)]"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <input
                type="text"
                value={submitForm.dataSubjectId}
                onChange={(e) => setSubmitForm((f) => ({ ...f, dataSubjectId: e.target.value }))}
                placeholder="Data Subject ID"
                aria-label="Data subject ID for review request"
                className="w-full bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)]"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <textarea
                value={submitForm.reason}
                onChange={(e) => setSubmitForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Reason for review request"
                aria-label="Reason for review request"
                rows={3}
                className="w-full bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)] resize-none"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitLoading || !submitForm.auditId.trim() || !submitForm.dataSubjectId.trim() || !submitForm.reason.trim()}
                aria-label="Submit review request"
                className="w-full text-white"
              >
                {submitLoading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
            {submitError && (
              <p role="alert" className="text-xs" style={{ color: GovernanceColors.critical }}>{submitError}</p>
            )}
            {submitResult && (
              <p role="status" className="text-xs" style={{ color: GovernanceColors.healthy }}>Request submitted -- ID: {submitResult}</p>
            )}
          </CardContent>
        </Card>

        {/* Data Subject Lookup */}
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Search aria-hidden="true" className="w-4 h-4" style={{ color: GovernanceColors.streaming }} />
              <h3 className="text-sm font-medium">Data Subject Lookup</h3>
            </div>
            <p className="text-xs text-[#8F857B]">View all governance actions associated with a data subject.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={lookupSubjectId}
                onChange={(e) => setLookupSubjectId(e.target.value)}
                placeholder="Data Subject ID"
                aria-label="Data subject ID for lookup"
                className="flex-1 bg-[#1C1917] border border-[#3A352F] rounded px-3 py-1.5 text-sm text-[#F5F0E8] placeholder-[#8F857B] focus:outline-none focus:border-[var(--gc-focus)]"
                style={{ '--gc-focus': GovernanceColors.primary } as React.CSSProperties}
              />
              <Button
                size="sm"
                onClick={handleLookup}
                disabled={lookupLoading || !lookupSubjectId.trim()}
                aria-label="Look up data subject actions"
                className="text-white"
              >
                {lookupLoading ? 'Loading...' : 'Lookup'}
              </Button>
            </div>
            {lookupError && (
              <p role="alert" className="text-xs" style={{ color: GovernanceColors.critical }}>{lookupError}</p>
            )}
            {subjectActions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" aria-label="Data subject actions">
                  <thead>
                    <tr className="border-b border-[#3A352F] text-[#A8A29E]">
                      <th className="text-left py-2 pr-3 font-medium">Audit ID</th>
                      <th className="text-left py-2 pr-3 font-medium">Action</th>
                      <th className="text-left py-2 pr-3 font-medium">Outcome</th>
                      <th className="text-left py-2 font-medium">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectActions.map((a, i) => (
                      <tr key={i} className="border-b border-[#2E2A26] hover:bg-[#3D3833]">
                        <td className="py-1.5 pr-3 font-mono text-[#F5F0E8]">{a.auditId}</td>
                        <td className="py-1.5 pr-3 text-[#F5F0E8]">{a.action}</td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className="text-xs px-1 py-0" style={{ color: statusColor(a.outcome), borderColor: statusColor(a.outcome) }}>
                            {a.outcome}
                          </Badge>
                        </td>
                        <td className="py-1.5 text-[#8F857B]">{new Date(a.timestamp).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!lookupLoading && subjectActions.length === 0 && lookupSubjectId && !lookupError && (
              <p className="text-xs text-[#8F857B]">No actions found for this data subject.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
